import { Text } from '@/components/text';
import { VehicleDelayStatus } from '@/components/vehicle-delay-status';
import { MODE_TRANSFER, MODE_WATER } from '@/lib/constants';
import { RouteScheduleSegmentInterface, RouteSegmentInterface } from '@/types';
import { formatDuration } from '@/utils/format';
import { formatToLocationDateTime } from '@/utils/format/date-time';
import { formatDelayStatusLabel } from '@/utils/strings';
import { tv } from '@/utils/styles';
import { getVehicleType } from '@/utils/vehicle';

interface Props {
  readonly segment: RouteSegmentInterface | RouteScheduleSegmentInterface;
  readonly isShortTransfer?: boolean;
}

const RouteTooltip = ({ segment, isShortTransfer }: Props) => {
  const { mode, description, duration, from, to } = segment;
  const { base, descriptionWrapper, alertBox, messageBox, dl, dt, dd, separator } = styles();

  const isTransfer = mode === MODE_TRANSFER;
  const isOceanMode = mode === MODE_WATER;
  const vehicleType = getVehicleType(mode);

  // Arrival, departure times for Tooltip (if a schedule bar)
  const hasDepartureDateTime = 'departureDateTime' in segment;
  const hasArrivalDateTime = 'arrivalDateTime' in segment;
  const departureTimezone = from?.timezoneName;
  const arrivalTimezone = to?.timezoneName || departureTimezone;

  // Get Segment Attributes
  const hasFlightNumber = 'attributes' in segment && segment.attributes.flightNumber;
  const hasVehicleName = 'attributes' in segment && segment.attributes.vehicleName;

  // Delays / Location message
  const delayStatus = 'attributes' in segment ? segment.attributes.vehicleDelay : undefined;
  const shouldShowDelayStatus = !isTransfer && isOceanMode && hasDepartureDateTime;

  return (
    <div className={base()}>
      {isShortTransfer && (
        <div className={alertBox()}>
          <Text className="m-0 text-sm">
            <b>Caution: Short Transfer.</b>
            <br />
            Please check viability with carrier.
          </Text>
        </div>
      )}
      {shouldShowDelayStatus && delayStatus && (
        <div className={messageBox()}>
          <VehicleDelayStatus delay={delayStatus} />
          <Text className="m-0 text-sm">{formatDelayStatusLabel(delayStatus)}</Text>
        </div>
      )}
      {shouldShowDelayStatus && !delayStatus && (
        <div className={messageBox()}>
          <Text className="m-0 text-sm italic opacity-50">No location data available</Text>
        </div>
      )}
      <div className={descriptionWrapper()}>
        <Text variant="secondary" className="m-0 font-medium">
          {description}
        </Text>
        <Text variant="sm" className="m-0 ml-4 whitespace-nowrap">
          {formatDuration(duration)}
        </Text>
      </div>
      {from && (
        <Text variant="sm" className="text-1 mb-0 mt-1 leading-snug">
          {from.name}
          {to?.name ? ` to ${to.name}` : ''}
        </Text>
      )}
      {(hasDepartureDateTime || hasArrivalDateTime) && !isTransfer && (
        <div className="mt-2">
          {hasDepartureDateTime && (
            <dl className={dl()}>
              <dt className={dt()}>Departs</dt>
              <dd className={dd()}>{formatToLocationDateTime(segment.departureDateTime, departureTimezone)}</dd>
            </dl>
          )}
          {hasArrivalDateTime && (
            <dl className={dl()}>
              <dt className={dt()}>Arrives</dt>
              <dd className={dd()}>
                {segment.arrivalDateTime
                  ? formatToLocationDateTime(segment.arrivalDateTime as Date, arrivalTimezone!)
                  : null}
              </dd>
            </dl>
          )}
          <span className={separator()} />
          {hasFlightNumber && (
            <dl className={dl()}>
              <dt className={dt()}>Flight #</dt>
              <dd className={dd()}>{segment.attributes.flightNumber}</dd>
            </dl>
          )}
          {hasVehicleName && (
            <dl className={dl()}>
              <dt className={dt()}>{vehicleType}</dt>
              <dd className={dd()}>{segment.attributes.vehicleName}</dd>
            </dl>
          )}
        </div>
      )}
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'min-w-60 max-w-72 p-sm text-left',
    descriptionWrapper: 'flex items-center justify-between',
    alertBox:
      '-m-4 -mt-3 mb-4 rounded-md rounded-b-none bg-states-warning-backgroundColor p-sm px-4 leading-snug text-text-primary [&_b]:font-medium',
    messageBox: '-m-4 -mt-3 mb-4 flex items-center rounded-md rounded-b-none bg-[#3f4449] p-sm px-4 leading-snug',
    dl: 'my-0 flex',
    dt: 'm-0 min-w-14 p-0 opacity-60',
    dd: 'm-0 p-0',
    separator: 'my-1 block w-full border-t border-white/[0.06]',
  },
});

export { RouteTooltip };
